import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PayeeConnectOnboardingStatus = "not_started" | "pending" | "complete";

export interface PayeeConnectStatus {
  stripe_connected_account_id: string | null;
  stripe_connect_onboarding_status: PayeeConnectOnboardingStatus;
  stripe_connect_payouts_enabled: boolean;
  stripe_connect_details_submitted: boolean;
}

const defaultStatus: PayeeConnectStatus = {
  stripe_connected_account_id: null,
  stripe_connect_onboarding_status: "not_started",
  stripe_connect_payouts_enabled: false,
  stripe_connect_details_submitted: false,
};

/** Returns payee's Stripe Connect status for Payouts UI and invoice Pay button. */
export function usePayeeConnectStatus(
  payeeType: "cleaner" | "company" | null | undefined,
  payeeId: string | null | undefined
) {
  return useQuery({
    queryKey: ["payee-connect", payeeType, payeeId],
    queryFn: async (): Promise<PayeeConnectStatus> => {
      if (!payeeType || !payeeId) return defaultStatus;
      const table = payeeType === "cleaner" ? "cleaners" : "cleaning_companies";
      const { data } = await supabase
        .from(table)
        .select(
          "stripe_connected_account_id, stripe_connect_onboarding_status, stripe_connect_payouts_enabled, stripe_connect_details_submitted"
        )
        .eq("id", payeeId)
        .single();

      const row = data as
        | {
            stripe_connected_account_id?: string | null;
            stripe_connect_onboarding_status?: PayeeConnectOnboardingStatus | null;
            stripe_connect_payouts_enabled?: boolean | null;
            stripe_connect_details_submitted?: boolean | null;
          }
        | null;
      if (!row) return defaultStatus;

      const status = (row.stripe_connect_onboarding_status ?? (row.stripe_connected_account_id ? "pending" : "not_started")) as PayeeConnectOnboardingStatus;
      return {
        stripe_connected_account_id: row.stripe_connected_account_id ?? null,
        stripe_connect_onboarding_status: status,
        stripe_connect_payouts_enabled: Boolean(row.stripe_connect_payouts_enabled),
        stripe_connect_details_submitted: Boolean(row.stripe_connect_details_submitted),
      };
    },
    enabled: !!payeeType && !!payeeId,
  });
}
